import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Maximize, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { langPath } from '../../utils/siteNav';
import { optimized, srcSetFor } from '../../utils/img';
import { roomSlug } from '../../i18n/roomPages';

// Compact card linking to a room's own page — used for "other rooms" on room pages.
export function RoomLinkCard({ room, labels }) {
  const { language } = useLanguage();
  const to = langPath(language, `rooms/${roomSlug(room)}`);
  return (
    <Link to={to} className="group block bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300" data-testid={`room-link-${roomSlug(room)}`}>
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={optimized(room.images?.[0], 800)} srcSet={srcSetFor(room.images?.[0], [400, 800])} sizes="(min-width: 1024px) 320px, 100vw" alt={room.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-slate-900 group-hover:text-amber-700 transition-colors">{room.name}</h3>
        <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5" />{room.size}</span>
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{room.guests}</span>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-slate-600">{labels.from} <strong className="text-slate-900">€{Math.round(room.price)}</strong> {labels.perNight}</span>
          <ArrowRight className="w-4 h-4 text-amber-600 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function OtherRooms({ rooms, labels }) {
  const { language } = useLanguage();
  if (!rooms.length) return null;
  return (
    <section className="bg-[#f5f1ea] border-t border-amber-100" data-testid="other-rooms">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h2 className="font-serif text-2xl md:text-3xl text-slate-900">{labels.otherRooms}</h2>
          <Link to={langPath(language, 'rooms')} className="text-sm text-amber-800 hover:text-amber-900 font-medium whitespace-nowrap" data-testid="all-rooms-link">{labels.allRooms} →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rooms.map((r) => <RoomLinkCard key={r.beds24Id || r.id} room={r} labels={labels} />)}
        </div>
      </div>
    </section>
  );
}
